/**
 * Eén doorgeefluik voor fouten die de app niet zelf kan opvangen.
 *
 * De app heeft (nog) geen monitoringdienst. In plaats van overal `console.error`
 * te strooien loopt alles hier langs, zodat er later precies één plek is waar
 * Sentry, Bugsnag of een eigen endpoint ingeprikt wordt:
 *
 *     setErrorReporter((error, report) => Sentry.captureException(error, { extra: report }));
 *
 * Roep `setErrorReporter` aan bij het opstarten (`src/main.tsx`) of laat hem leeg;
 * de console-regel blijft er hoe dan ook, want wie een fout meldt, heeft iets
 * nodig om door te geven.
 */

export interface ErrorReport {
    /** Waar de fout vandaan kwam: `app` (de hele schil) of de route van het scherm. */
    scope: string;
    /** De React-componentstack uit `componentDidCatch`, als die er is. */
    componentStack?: string;
}

export type ErrorReporter = (error: unknown, report: ErrorReport) => void;

let reporter: ErrorReporter | null = null;

/** Koppelt een monitoringdienst aan. `null` haalt hem er weer af. */
export function setErrorReporter(next: ErrorReporter | null): void {
    reporter = next;
}

/** Geeft een fout door aan de monitoring en laat hem altijd in de console achter. */
export function reportError(error: unknown, report: ErrorReport): void {
    try {
        reporter?.(error, report);
    } catch (reporterError) {
        /* Een kapotte monitoring mag nooit de fout overschaduwen die hij moest melden. */
        console.error('[smartkeur] de foutrapportage zelf faalde', reporterError);
    }

    console.error(`[smartkeur:${report.scope}]`, error);
}

/** Korte, leesbare regel over een fout - voor de uitklapper in `CrashPanel`. */
export function describeError(error: unknown): string {
    if (error instanceof Error) {
        return `${error.name}: ${error.message}`;
    }

    if (typeof error === 'string') {
        return error;
    }

    try {
        return JSON.stringify(error);
    } catch {
        return String(error);
    }
}
